import type { Profession } from "@brookbush/types";

import type { WizardFormValues } from "../onboardingWizardForm";

const CATA_SLUG = "certified-athletic-therapist";
const OT_SLUG = "occupational-therapy";

// Resolves the gating conditions for the profession section: whether a CATA
// member number is required and whether the OT disclaimer must be shown.
export const professionGating = (
	values: Pick<
		WizardFormValues,
		"primaryProfessionId" | "secondaryProfessionId"
	>,
	professions: Profession[] | undefined,
) => {
	const slugOf = (id?: string | null) =>
		professions?.find((p) => p._id === id)?.slug;

	const slugs = [
		slugOf(values.primaryProfessionId),
		slugOf(values.secondaryProfessionId),
	];

	return {
		requiresCataMemberNumber: slugs.includes(CATA_SLUG),
		showDisclaimer: slugs.includes(OT_SLUG),
	};
};

// The profession section is complete when a primary profession is chosen and
// any conditional requirements (CATA number, accepted OT disclaimer) are met.
export const isProfessionComplete = (
	values: Pick<
		WizardFormValues,
		"primaryProfessionId" | "secondaryProfessionId" | "cataMemberNumber"
	>,
	professions: Profession[] | undefined,
	isDisclaimerAccepted: boolean,
) => {
	const { requiresCataMemberNumber, showDisclaimer } = professionGating(
		values,
		professions,
	);

	return (
		!!values.primaryProfessionId &&
		(requiresCataMemberNumber ? !!values.cataMemberNumber : true) &&
		(showDisclaimer ? isDisclaimerAccepted : true)
	);
};

export const isCertificationComplete = (
	values: Pick<WizardFormValues, "selectedCertificationId">,
) => !!values.selectedCertificationId;

// The accreditation ids the user has marked as a CEU goal.
export const ceuSelectedIds = (values: Pick<WizardFormValues, "ceuGoals">) =>
	Object.entries(values.ceuGoals ?? {})
		.filter(([, goal]) => goal?.selected)
		.map(([id]) => id);

export const isCeuComplete = (values: Pick<WizardFormValues, "ceuGoals">) =>
	ceuSelectedIds(values).length > 0;
